import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Popover,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import AddIcon from "@mui/icons-material/Add";
import {
  getProjectId,
  getProjectName,
  getProjectBallBackground,
} from "../utils/projectUtils";
// import CreateActionIcon from "../../../ui/components/CreateActionIcon";
import GlassSearchBar from "../../../ui/components/GlassSearchBar";
import useScrollFade from "../../../ui/hooks/useScrollFade";
import {
  CONVERSATIONS_OPENED_EVENT,
  dispatchMinimizeConversations,
  EXPAND_PROJECTS_EVENT,
  MINIMIZE_PROJECTS_EVENT,
} from "../../sidebarAccordionEvents";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
  homeGlassHero,
} from "../../../pages/Home/constants/homeGlass";
import { getProjectWorkRoomByProjectId } from "../../../core/services/projectWorkRoom.service";
import { useAuth } from "../../../app/context/AuthContext";
import { MODULE_LIST } from "../../../constants/commons";

function unwrapWorkroom(payload) {
  if (!payload) return null;
  if (Array.isArray(payload)) return payload.find(Boolean) ?? null;
  if (payload?.data && typeof payload.data === "object") return payload.data;
  return payload;
}

export default function HeaderProjectsPanel({
  activeProjectId,
  openClientKbModal,
  openProjectKbModal,
  handleProjectClick,
  handleWorkroomClick,
  onNewWorkroom,
  loading,
  isGenerating,
  kbUploading,
  activeProject,
  isProjectDetailsOpen,
  filteredProjects,
  filteredArchivedProjects = [],
  activeProjectName,
  activeProjectSourcesCount,
  projectSearch,
  setProjectSearch,
  isProjectSearchOpen,
  setIsProjectSearchOpen,
  isNewProjectInputOpen,
  newProjectDraftTitle,
  handleInlineProjectInputChange,
  submitInlineProject,
  closeNewProjectFlow,
  handleProjectListClick,
  handleProjectDeleteClick,
  deleteAnchorEl,
  pendingDeleteProject,
  closeDeletePopover,
  confirmDeleteProject,
  deletingProjectIds,
  openNewProjectPill,
  handleProjectsLabelClick,
  onNewProject,
  onEditProject,
  isWorkroomRoute = false,
  workroomRefreshKey = 0,
  onProjectsSectionOpenChange,
}) {
  const [isProjectsSectionOpen, setIsProjectsSectionOpen] = React.useState(false);
  const [activeProjectHasWorkroom, setActiveProjectHasWorkroom] =
    React.useState(false);
  const [isProjectListExpanded, setIsProjectListExpanded] = React.useState(false);
  const [isArchivedSectionOpen, setIsArchivedSectionOpen] = React.useState(false);
  const [projectFocusTargetId, setProjectFocusTargetId] = React.useState(null);
  const [projectMenuAnchor, setProjectMenuAnchor] = React.useState(null);
  const [projectMenuTarget, setProjectMenuTarget] = React.useState(null);
  const [isConversationPanelOpen, setIsConversationPanelOpen] =
    React.useState(false);
  const wasConversationPanelOpenRef = React.useRef(false);
  const panelRef = React.useRef(null);
  const activeProjectListItemRef = React.useRef(null);
  const activeArchivedProjectListItemRef = React.useRef(null);
  const projectListScroll = useScrollFade({ disabled: !isProjectsSectionOpen });
  const archivedProjectListScroll = useScrollFade({
    disabled: !isArchivedSectionOpen,
  });
  const isProjectsCompact = isConversationPanelOpen && isProjectsSectionOpen;
  const visibleCompactProjects = React.useMemo(() => {
    if (!isProjectsCompact) return filteredProjects;
    if (activeProjectId != null) {
      const active = filteredProjects.find(
        (project) => String(getProjectId(project)) === String(activeProjectId),
      );
      if (active) {
        const nextProject = filteredProjects.find(
          (project) => String(getProjectId(project)) !== String(activeProjectId),
        );
        // Show selected card plus at least one other project before "+N more".
        return nextProject ? [active, nextProject] : [active];
      }
    }
    return filteredProjects.slice(0, 1);
  }, [isProjectsCompact, filteredProjects, activeProjectId]);
  const hiddenCompactProjectCount = isProjectsCompact
    ? Math.max(0, filteredProjects.length - visibleCompactProjects.length)
    : 0;
  const projectsToRender =
    isProjectsCompact && !isProjectListExpanded
      ? visibleCompactProjects
      : filteredProjects;

  const { userPermissions } = useAuth()
      
  const projectModule = Object.hasOwn(userPermissions, MODULE_LIST.PROJECT) ? userPermissions[MODULE_LIST.PROJECT] : false;

  let canViewProjects = false;
  let canEditProjects = false;
  if (projectModule && (projectModule.edit || projectModule.view)) {
    canViewProjects = true;
    canEditProjects = projectModule.edit;
  }
    
  React.useEffect(() => {
    if (activeProjectId == null || String(activeProjectId).trim() === "") {
      setActiveProjectHasWorkroom(false);
      return undefined;
    }

    let cancelled = false;

    const loadWorkroomStatus = async () => {
      try {
        const response = await getProjectWorkRoomByProjectId(activeProjectId);
        if (!cancelled) {
          setActiveProjectHasWorkroom(Boolean(unwrapWorkroom(response)));
        }
      } catch {
        if (!cancelled) setActiveProjectHasWorkroom(false);
      }
    };

    loadWorkroomStatus();

    return () => {
      cancelled = true;
    };
  }, [activeProjectId, workroomRefreshKey]);

  const showActiveProjectFullBox =
    isProjectsCompact &&
    !isProjectListExpanded &&
    activeProjectId != null &&
    projectsToRender.some(
      (project) => String(getProjectId(project)) === String(activeProjectId),
    );
  const canExpandProjectList =
    !isProjectsCompact &&
    (filteredProjects.length > 5 ||
      projectListScroll.hasOverflow ||
      isProjectListExpanded);
  const projectListMaxHeight = isProjectsCompact
    ? isProjectListExpanded
      ? "14vh"
      : showActiveProjectFullBox
        ? "none"
        : 30
    : isProjectListExpanded
      ? "30vh"
      : 150;

  const requestProjectFocus = React.useCallback((project) => {
    const projectId = getProjectId(project);
    if (projectId == null) return;
    setProjectFocusTargetId(String(projectId));
  }, []);

  const wasWorkroomRouteRef = React.useRef(isWorkroomRoute);
  React.useEffect(() => {
    if (isWorkroomRoute) {
      setIsProjectsSectionOpen(false);
      setIsProjectSearchOpen(false);
      setIsProjectListExpanded(false);
      setIsArchivedSectionOpen(false);
    } else if (wasWorkroomRouteRef.current) {
      // Leaving workroom for a silo/home — keep Projects expanded.
      setIsProjectsSectionOpen(true);
    }
    wasWorkroomRouteRef.current = isWorkroomRoute;
  }, [isWorkroomRoute, setIsProjectSearchOpen]);

  React.useEffect(() => {
    const expandProjects = () => setIsProjectsSectionOpen(true);
    window.addEventListener(EXPAND_PROJECTS_EVENT, expandProjects);
    return () => window.removeEventListener(EXPAND_PROJECTS_EVENT, expandProjects);
  }, []);

  React.useEffect(() => {
    if (!canExpandProjectList) {
      setIsProjectListExpanded(false);
    }
  }, [canExpandProjectList]);

  React.useEffect(() => {
    if (!isProjectsSectionOpen) {
      setIsProjectListExpanded(false);
    }
  }, [isProjectsSectionOpen]);

  React.useEffect(() => {
    onProjectsSectionOpenChange?.(isProjectsSectionOpen);
  }, [isProjectsSectionOpen, onProjectsSectionOpenChange]);

  React.useEffect(() => {
    const handleConversationsChange = (event) => {
      setIsConversationPanelOpen(Boolean(event?.detail?.open));
    };

    const minimizeProjectsContent = () => {
      setIsProjectSearchOpen(false);
      setIsProjectListExpanded(false);
      setIsArchivedSectionOpen(false);
    };

    window.addEventListener(
      CONVERSATIONS_OPENED_EVENT,
      handleConversationsChange,
    );
    window.addEventListener(MINIMIZE_PROJECTS_EVENT, minimizeProjectsContent);

    return () => {
      window.removeEventListener(
        CONVERSATIONS_OPENED_EVENT,
        handleConversationsChange,
      );
      window.removeEventListener(MINIMIZE_PROJECTS_EVENT, minimizeProjectsContent);
      setIsConversationPanelOpen(false);
    };
  }, [setIsProjectSearchOpen]);

  React.useEffect(() => {
    const justOpened =
      isConversationPanelOpen && !wasConversationPanelOpenRef.current;
    wasConversationPanelOpenRef.current = isConversationPanelOpen;

    if (justOpened && isProjectsSectionOpen) {
      setIsProjectSearchOpen(false);
      setIsProjectListExpanded(false);
      setIsArchivedSectionOpen(false);
    }
  }, [isConversationPanelOpen, isProjectsSectionOpen, setIsProjectSearchOpen]);

  React.useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--header-project-list-extra-offset",
      isProjectsSectionOpen &&
        isProjectListExpanded &&
        canExpandProjectList &&
        !isConversationPanelOpen
        ? "18vh"
        : "0px",
    );

    return () => {
      root.style.setProperty("--header-project-list-extra-offset", "0px");
    };
  }, [
    canExpandProjectList,
    isConversationPanelOpen,
    isProjectListExpanded,
    isProjectsSectionOpen,
  ]);

  React.useEffect(() => {
    if (
      !activeProjectId ||
      !isProjectsSectionOpen ||
      String(activeProjectId) !== String(projectFocusTargetId ?? "")
    ) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      const activeProjectListItem = activeProjectListItemRef.current;
      const activeArchivedProjectListItem = activeArchivedProjectListItemRef.current;
      const activeNode = activeProjectListItem ?? activeArchivedProjectListItem;
      const scrollContainer = activeProjectListItem
        ? projectListScroll.scrollRef.current
        : archivedProjectListScroll.scrollRef.current;

      if (!activeNode || !scrollContainer) return;

      activeNode.focus?.({ preventScroll: true });

      const nodeRect = activeNode.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const targetScrollTop =
        scrollContainer.scrollTop + nodeRect.top - containerRect.top;

      scrollContainer.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: "smooth",
      });
      setProjectFocusTargetId(null);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [
    activeProjectId,
    archivedProjectListScroll.scrollRef,
    isArchivedSectionOpen,
    isProjectDetailsOpen,
    isProjectListExpanded,
    isProjectsSectionOpen,
    projectFocusTargetId,
    projectListScroll.scrollRef,
  ]);

  React.useEffect(() => {
    const node = panelRef.current;
    if (!node) return undefined;

    const root = document.documentElement;
    let frameId = null;
    const updateProjectPanelBottom = () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const { bottom } = node.getBoundingClientRect();
        root.style.setProperty("--header-project-panel-bottom", `${bottom + 12}px`);
      });
    };

    updateProjectPanelBottom();
    window.addEventListener("resize", updateProjectPanelBottom);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateProjectPanelBottom);
      resizeObserver.observe(node);
    }

    return () => {
      if (frameId != null) window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", updateProjectPanelBottom);
      resizeObserver?.disconnect();
      root.style.removeProperty("--header-project-panel-bottom");
    };
  }, []);

  const toggleProjectsSection = () => {
    setIsProjectsSectionOpen((prev) => {
      const nextOpen = !prev;
      if (nextOpen) {
        dispatchMinimizeConversations();
      } else {
        setIsProjectSearchOpen(false);
      }
      return nextOpen;
    });
  };

  const handleOpenNewProjectPill = () => {
    setIsProjectsSectionOpen(true);
    if (onNewProject) {
      onNewProject();
      return;
    }
    openNewProjectPill();
  };

  const handleProjectSearchOpenChange = (nextOpen) => {
    if (nextOpen) {
      setIsProjectsSectionOpen(true);
    }
    setIsProjectSearchOpen(nextOpen);
  };

  const closeProjectMenu = () => {
    setProjectMenuAnchor(null);
    setProjectMenuTarget(null);
  };

  const handleOpenProjectMenu = (event, project) => {
    event.stopPropagation();
    event.preventDefault();
    const rowEl = event.currentTarget?.closest?.("[data-project-row]");
    setProjectMenuAnchor(rowEl || event.currentTarget);
    setProjectMenuTarget(project);
  };

  const handleProjectMenuEdit = (event) => {
    event.stopPropagation();
    const project = projectMenuTarget;
    closeProjectMenu();
    onEditProject?.(project);
  };

  const handleProjectMenuDelete = (event) => {
    event.stopPropagation();
    const project = projectMenuTarget;
    const anchor = projectMenuAnchor;
    closeProjectMenu();
    if (!project) return;
    handleProjectDeleteClick(null, project, anchor);
  };

  const renderProjectActionsButton = (project, { isActive = false, isBusy = false, inline = false } = {}) => {
    const projectId = getProjectId(project);
    if (projectId == null) return null;

    const isDeleting = !!deletingProjectIds[String(projectId)];

    return (
      <IconButton
        className="project-actions-btn"
        size="small"
        disabled={isDeleting || isBusy}
        onClick={(event) => handleOpenProjectMenu(event, project)}
        sx={{
          ...(inline
            ? { flexShrink: 0 }
            : {
                position: "absolute",
                right: 4,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
              }),
          display: canEditProjects ? "block" : "none",
          p: 0.2,
          width: 22,
          height: 22,
          opacity: isDeleting || isBusy ? 1 : isActive ? 0.9 : 0,
          color: "rgba(255,255,255,0.45)",
          transition: "opacity 160ms ease, color 160ms ease",
          "&:hover": { color: "#fff" },
          "&.Mui-disabled": { color: "rgba(255,255,255,0.35)" },
        }}
        title={isDeleting ? "Deleting..." : "Project actions"}
      >
        {isDeleting ? (
          <CircularProgress size={11} thickness={5.5} color="inherit" />
        ) : (
          <MoreHorizIcon sx={{ fontSize: 14 }} />
        )}
      </IconButton>
    );
  };

  return (
    <>
      <Box
        ref={panelRef}
        sx={{
          width: "100%",
          flexShrink: isWorkroomRoute && isProjectsSectionOpen ? 1 : 0,
          minHeight: isWorkroomRoute && isProjectsSectionOpen ? 0 : undefined,
          maxHeight: isWorkroomRoute && isProjectsSectionOpen ? "100%" : undefined,
          overflowY: isWorkroomRoute && isProjectsSectionOpen ? "auto" : undefined,
          transition: "margin 220ms ease",
          ...(isWorkroomRoute && isProjectsSectionOpen
            ? {
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(255,255,255,0.3)",
                  borderRadius: "4px",
                },
              }
            : null),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: isProjectsCompact ? 0.45 : 1.05,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexShrink: 0,
              maxWidth: isProjectSearchOpen ? 0 : 150,
              overflow: "hidden",
              whiteSpace: "nowrap",
              opacity: isProjectSearchOpen ? 0.72 : 1,
              transition:
                "max-width 240ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease",
              minWidth: 0,
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              aria-expanded={isProjectsSectionOpen}
              aria-label={
                isProjectsSectionOpen ? "Collapse projects" : "Expand projects"
              }
              onClick={toggleProjectsSection}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleProjectsSection();
                }
              }}
              sx={{
                display: canViewProjects ? "inline-flex" : "none",
                alignItems: "center",
                gap: 0.25,
                color: "rgba(234,231,226,0.45)",
                cursor: "pointer",
                userSelect: "none",
                "&:hover": { color: "rgba(234,231,226,0.85)" },
              }}
            >
              <ChevronRightIcon
                sx={{
                  fontSize: 16,
                  color: "inherit",
                  transform: isProjectsSectionOpen ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform 160ms ease",
                }}
              />
              <Typography
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "inherit",
                }}
              >
                Projects
              </Typography>
            </Box>
            {isProjectsSectionOpen && (
              <Box
                component="button"
                type="button"
                aria-label="Create new project"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenNewProjectPill();
                }}
                sx={{
                  width: 22,
                  height: 22,
                  p: 0,
                  border: 0,
                  borderRadius: "5px",
                  display:  canEditProjects ? "inline-flex" : 'none',
                  alignItems: "center",
                  justifyContent: "center",
                  background: "transparent",
                  color: "rgba(234,231,226,0.75)",
                  cursor: "pointer",
                  "&:hover": { color: "#fff" },
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </Box>
            )}
          </Box>
          {isProjectsSectionOpen && (
            <GlassSearchBar
              value={projectSearch}
              onChange={setProjectSearch}
              open={isProjectSearchOpen}
              onOpenChange={handleProjectSearchOpenChange}
              height={28}
              collapsedSize={24}
              placeholder="Search projects..."
              ariaLabel="Search projects"
              openTooltip="Search projects"
              inputSx={{
                fontSize: 11,
                "&::placeholder": {
                  color: "rgba(234,231,226,0.54)",
                },
              }}
              sx={{
                ml: "auto",
                flex: isProjectSearchOpen ? "1 1 100%" : "0 0 24px",
                borderRadius: isProjectSearchOpen ? "999px" : "6px",
              }}
            />
          )}
        </Box>

        {isWorkroomRoute && !isProjectsSectionOpen ? (
          <Box
            sx={{
              position: "relative",
              mt: 1.1,
              mb: 0.35,
              minWidth: 0,
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              aria-label={`Open projects, current project ${activeProjectName}`}
              onClick={toggleProjectsSection}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleProjectsSection();
                }
              }}
              sx={{
                position: "relative",
                px: 1.2,
                py: 1.05,
                borderRadius: "12px",
                cursor: "pointer",
                userSelect: "none",
                background:
                  "linear-gradient(140deg, rgba(255,255,255,0.14), rgba(255,255,255,0.05))",
                border: "1px solid rgba(255,255,255,0.12)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                boxShadow: "0 10px 22px rgba(0,0,0,0.14)",
                transition:
                  "border-color 160ms ease, background 160ms ease, box-shadow 160ms ease",
                "&:hover": {
                  borderColor: "rgba(255,255,255,0.2)",
                  background:
                    "linear-gradient(140deg, rgba(255,255,255,0.18), rgba(255,255,255,0.07))",
                  boxShadow: "0 12px 26px rgba(0,0,0,0.18)",
                },
                "&:focus-visible": {
                  outline: "none",
                  borderColor: "rgba(255,255,255,0.28)",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.85,
                  minWidth: 0,
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 8,
                    height: 8,
                    minWidth: 8,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: getProjectBallBackground(activeProject),
                    boxShadow: "0 0 10px rgba(255,255,255,0.12)",
                  }}
                />
                <Typography
                  component="h2"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 16,
                    fontWeight: 500,
                    lineHeight: 1.25,
                    letterSpacing: "-0.01em",
                    color: "#fff",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {activeProjectName || "Untitled project"}
                </Typography>
              </Box>
              {/* <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.45,
                  mt: 0.7,
                  pl: 1.7,
                  color: "rgba(255,255,255,0.5)",
                }}
              >
                <WorkspacesOutlinedIcon sx={{ fontSize: 12.5 }} />
                <Typography
                  component="span"
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    lineHeight: 1.2,
                  }}
                >
                  Workroom
                </Typography>
              </Box> */}
            </Box>
          </Box>
        ) : null}

        {isProjectsSectionOpen && (
          <>
            {isNewProjectInputOpen ? (
              <Box
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    closeNewProjectFlow();
                  }
                  if (e.key === "Enter" && !loading && !isGenerating) {
                    e.preventDefault();
                    submitInlineProject();
                  }
                }}
                sx={{
                  width: "100%",
                  boxSizing: "border-box",
                  mb: 1.25,
                  px: 1.2,
                  py: 0.55,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.9,
                  borderRadius: "10px",
                  bgcolor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  cursor: "default",
                  opacity: loading || isGenerating ? 0.65 : 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: loading || isGenerating
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(255,255,255,0.12)",
                    borderColor: loading || isGenerating
                      ? "rgba(255,255,255,0.12)"
                      : "rgba(255,255,255,0.18)",
                  },
                  "&:focus-visible": {
                    outline: "none",
                    borderColor: "rgba(255,255,255,0.26)",
                  },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: "linear-gradient(90deg, #8ec5fc 0%, #c8e6c9 45%, #fff9c4 100%)",
                    boxShadow: "0 0 10px rgba(142, 197, 252, 0.4)",
                  }}
                />
                <Box
                  component="input"
                  value={newProjectDraftTitle}
                  onChange={handleInlineProjectInputChange}
                  onBlur={() => {
                    if (newProjectDraftTitle.trim()) {
                      submitInlineProject();
                      return;
                    }
                    closeNewProjectFlow();
                  }}
                  placeholder="New Project"
                  aria-label="New Project"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: "0.01em",
                    color: "rgba(255,255,255,0.96)",
                    fontFamily:
                      "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                    "&::placeholder": {
                      color: "rgba(255,255,255,0.96)",
                      opacity: 1,
                    },
                  }}
                />
                <Box
                  component="span"
                  role="button"
                  tabIndex={-1}
                  aria-label="Upload file"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!loading && !isGenerating && !kbUploading) {
                      openClientKbModal();
                    }
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!loading && !isGenerating && !kbUploading) {
                        openClientKbModal();
                      }
                    }
                  }}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    ml: "auto",
                    cursor:
                      loading || isGenerating || kbUploading
                        ? "not-allowed"
                        : "pointer",
                    color: "rgba(255,255,255,0.92)",
                  }}
                >
                  <FileUploadOutlinedIcon sx={{ fontSize: 18, color: "inherit" }} />
                </Box>
              </Box>
            ) : null}
            <Box
              ref={projectListScroll.scrollRef}
              onScroll={projectListScroll.onScroll}
              sx={{
                maxHeight: projectListMaxHeight,
                overflowY: showActiveProjectFullBox ? "visible" : "auto",
                pr: 0.15,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                gap: 0,
                transition: "max-height 260ms cubic-bezier(0.22, 1, 0.36, 1)",
                ...projectListScroll.scrollFadeSx,
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(255,255,255,0.3)",
                  borderRadius: "4px",
                },
              }}
            >
              {projectsToRender.map((project) => {
              const projectId = getProjectId(project);
              const projectName = getProjectName(project);
              const isActive = String(projectId) === String(activeProjectId);
              const isActiveDetailsOpen =
                isActive &&
                (isProjectDetailsOpen || (isProjectsCompact && !isProjectListExpanded));

              if (isActiveDetailsOpen) {
                return (
                  <Box
                    key={projectId ?? projectName}
                    ref={isActive ? activeProjectListItemRef : null}
                    tabIndex={isActive ? -1 : undefined}
                    data-project-row
                    sx={{
                      mb: 1.25,
                      p: 1.25,
                      borderRadius: 3,
                      position: "relative",
                      background:
                        "linear-gradient(140deg, rgba(255,255,255,0.16), rgba(255,255,255,0.06))",
                      border: "1px solid rgba(255,255,255,0.14)",
                      boxShadow: "0 12px 24px rgba(0, 0, 0, 0.22)",
                      "&:hover .project-actions-btn": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <Typography
                        onClick={() => {
                          handleProjectsLabelClick();
                        }}
                        sx={{
                          fontSize: 15,
                          color: "#fff",
                          fontWeight: 500,
                          lineHeight: 1.2,
                          cursor: "pointer",
                          flex: 1,
                          minWidth: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {projectName || activeProjectName || "New Project Title"}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Project settings"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onEditProject?.(project);
                        }}
                        sx={{
                          color: "rgba(255,255,255,0.9)",
                          display: canEditProjects ? "block" : "none",
                          p: 0.25,
                          flexShrink: 0,
                        }}
                      >
                        <MoreHorizIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          color: "rgba(255,255,255,0.76)",
                          cursor: "pointer",
                          minWidth: 0,
                          "&:hover": { color: "#fff" },
                        }}
                        onClick={() => {
                          handleWorkroomClick?.(project);
                        }}
                      >
                        <WorkspacesOutlinedIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: 12 }}>Workroom</Typography>
                      </Box>
                      {!activeProjectHasWorkroom ? (
                        <Tooltip title="Create workroom">
                          <IconButton
                            size="small"
                            aria-label="Create workroom"
                            disabled={loading || isGenerating}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              onNewWorkroom?.(project);
                            }}
                            sx={{
                              width: 22,
                              height: 22,
                              p: 0,
                              color: "rgba(255,255,255,0.72)",
                              border: "1px solid rgba(255,255,255,0.14)",
                              background: "rgba(255,255,255,0.05)",
                              "&:hover": {
                                color: "#111",
                                background: "#D4A017",
                                borderColor: "#D4A017",
                              },
                              "&.Mui-disabled": {
                                color: "rgba(255,255,255,0.22)",
                                borderColor: "rgba(255,255,255,0.06)",
                              },
                            }}
                          >
                            <AddIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box
                        onClick={() => {
                          if (projectId != null) openProjectKbModal(projectId);
                          else openClientKbModal();
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          color: "rgba(255,255,255,0.85)",
                          cursor: "pointer",
                          "&:hover": { color: "#fff" },
                        }}
                      >
                        <FileUploadOutlinedIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: 12 }}>Upload</Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 11,
                          flexShrink: 0,
                          color: "rgba(255,255,255,0.65)",
                        }}
                      >
                        {activeProjectSourcesCount != null
                          ? `${activeProjectSourcesCount} sources`
                          : "sources"}
                      </Typography>
                    </Box>
                  </Box>
                );
              }

              return (
                <Box
                  key={projectId ?? projectName}
                  ref={isActive ? activeProjectListItemRef : null}
                  tabIndex={isActive ? -1 : undefined}
                  data-project-row
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.65,
                    minHeight: 20,
                    py: 0.1,
                    px: 0.75,
                    pr: 3,
                    borderRadius: "7px",
                    minWidth: 0,
                    background: isActive
                      ? "linear-gradient(90deg, rgba(255,255,255,0.13), rgba(255,255,255,0.045))"
                      : "transparent",
                    border: isActive
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid transparent",
                    backdropFilter: isActive ? "blur(8px)" : "none",
                    transition:
                      "background 160ms ease, border-color 160ms ease, color 160ms ease",
                    "&:hover": {
                      background: isActive
                        ? "linear-gradient(90deg, rgba(255,255,255,0.15), rgba(255,255,255,0.06))"
                        : "rgba(255,255,255,0.055)",
                    },
                    "&:hover .project-actions-btn": {
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 5,
                      height: 5,
                      minWidth: 5,
                      minHeight: 5,
                      borderRadius: "50%",
                      flexShrink: 0,
                      mt: "2px",
                      background: getProjectBallBackground(project),
                      boxShadow: "none",
                    }}
                  />
                  <Typography
                    onClick={() => {
                      requestProjectFocus(project);
                      handleProjectListClick(project);
                    }}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 11,
                      lineHeight: 1.25,
                      fontWeight: isActive ? 600 : 500,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: isActive ? "#fff" : "rgba(234,231,226,0.88)",
                      "&:hover": {
                        color: "#fff",
                      },
                    }}
                  >
                    {projectName}
                  </Typography>
                  {renderProjectActionsButton(project, { isActive })}
                </Box>
              );
              })}
            </Box>
            {isProjectsCompact && hiddenCompactProjectCount > 0 && !isProjectListExpanded && (
              <Typography
                component="button"
                type="button"
                onClick={() => setIsProjectListExpanded(true)}
                sx={{
                  mt: 0.35,
                  p: 0,
                  border: 0,
                  background: "transparent",
                  fontSize: 10,
                  lineHeight: 1.2,
                  color: "rgba(234,231,226,0.52)",
                  cursor: "pointer",
                  textAlign: "left",
                  "&:hover": { color: "rgba(234,231,226,0.9)" },
                }}
              >
                +{hiddenCompactProjectCount} more project
                {hiddenCompactProjectCount === 1 ? "" : "s"}
              </Typography>
            )}
            {isProjectsCompact && isProjectListExpanded && (
              <Typography
                component="button"
                type="button"
                onClick={() => setIsProjectListExpanded(false)}
                sx={{
                  mt: 0.35,
                  p: 0,
                  border: 0,
                  background: "transparent",
                  fontSize: 10,
                  lineHeight: 1.2,
                  color: "rgba(234,231,226,0.52)",
                  cursor: "pointer",
                  textAlign: "left",
                  "&:hover": { color: "rgba(234,231,226,0.9)" },
                }}
              >
                Show less
              </Typography>
            )}
            {canExpandProjectList && (
              <Tooltip
                title={isProjectListExpanded ? "Collapse list" : "Expand list"}
                placement="left"
              >
                <IconButton
                  size="small"
                  aria-label={
                    isProjectListExpanded ? "Collapse project list" : "Expand project list"
                  }
                  onClick={() => setIsProjectListExpanded((prev) => !prev)}
                  sx={{
                    mt: 0.6,
                    p: 0,
                    width: 18,
                    height: 18,
                    color: "rgba(234,231,226,0.62)",
                    "&:hover": {
                      color: "#fff",
                      background: "transparent",
                    },
                  }}
                >
                  {isProjectListExpanded ? (
                    <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {!isProjectsCompact && (
            <>
            <Divider
              sx={{
                mt: 1.5,
                mb: 1,
                borderColor: "rgba(255,255,255,0.15)",
              }}
            />

            <Box
              role="button"
              tabIndex={0}
              aria-expanded={isArchivedSectionOpen}
              onClick={() => setIsArchivedSectionOpen((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsArchivedSectionOpen((prev) => !prev);
                }
              }}
              sx={{
                width: "100%",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                minHeight: 28,
                px: 0.75,
                py: 0.5,
                borderRadius: "8px",
                border: "1px solid rgba(255,255,255,0.07)",
                background: "rgba(255,255,255,0.025)",
                cursor: "pointer",
                userSelect: "none",
                mb: isArchivedSectionOpen ? 0.75 : 0,
                transition: "background 160ms ease, border-color 160ms ease",
                "&:hover": {
                  background: "rgba(255,255,255,0.055)",
                  borderColor: "rgba(255,255,255,0.12)",
                },
                "&:hover .archived-label": {
                  color: "rgba(234,231,226,0.85)",
                },
              }}
            >
              <ArchiveOutlinedIcon
                sx={{
                  fontSize: 13,
                  color: "rgba(234,231,226,0.52)",
                  flexShrink: 0,
                }}
              />
              <Typography
                className="archived-label"
                sx={{
                  fontSize: 12,
                  color: "rgba(234,231,226,0.45)",
                  lineHeight: 1.2,
                  flex: 1,
                }}
              >
                Archived
              </Typography>
              {isArchivedSectionOpen ? (
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 16, color: "rgba(234,231,226,0.45)" }}
                />
              ) : (
                <ChevronRightIcon
                  sx={{ fontSize: 16, color: "rgba(234,231,226,0.45)" }}
                />
              )}
            </Box>

            {isArchivedSectionOpen && (
              <Box
                ref={archivedProjectListScroll.scrollRef}
                aria-label="Archived projects"
                onScroll={archivedProjectListScroll.onScroll}
                sx={{
                  minHeight: 4,
                  maxHeight: "14vh",
                  overflowY: "auto",
                  pr: 0.75,
                  ...archivedProjectListScroll.scrollFadeSx,
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(255,255,255,0.3)",
                    borderRadius: "4px",
                  },
                }}
              >
                {filteredArchivedProjects.length ? (
                  filteredArchivedProjects.map((project) => {
                    const projectId = getProjectId(project);
                    const projectName = getProjectName(project);
                    const isActive = String(projectId) === String(activeProjectId);

                    return (
                      <Box
                        key={projectId ?? projectName}
                        ref={isActive ? activeArchivedProjectListItemRef : null}
                        tabIndex={isActive ? -1 : undefined}
                        data-project-row
                        sx={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.45,
                          py: 0.15,
                          px: 0.6,
                          pr: 3,
                          borderRadius: "8px",
                          minWidth: 0,
                          background: isActive
                            ? "linear-gradient(140deg, rgba(255,255,255,0.14), rgba(255,255,255,0.05))"
                            : "transparent",
                          border: isActive
                            ? "1px solid rgba(255,255,255,0.18)"
                            : "1px solid transparent",
                          backdropFilter: isActive ? "blur(8px)" : "none",
                          "&:hover .project-actions-btn": {
                            opacity: 1,
                          },
                        }}
                      >
                        <Box
                          aria-hidden
                          sx={{
                            width: 5,
                            height: 5,
                            minWidth: 5,
                            minHeight: 5,
                            borderRadius: "50%",
                            flexShrink: 0,
                            mt: "2px",
                            background: getProjectBallBackground(project),
                            boxShadow: "none",
                            opacity: 0.65,
                          }}
                        />
                        <Typography
                          onClick={async () => {
                            requestProjectFocus(project);
                            handleProjectListClick(project);
                          }}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 11.5,
                            lineHeight: 1.45,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: isActive ? "#fff" : "rgba(234,231,226,0.62)",
                            "&:hover": {
                              color: "#fff",
                            },
                          }}
                        >
                          {projectName}
                        </Typography>
                        {renderProjectActionsButton(project, { isActive })}
                      </Box>
                    );
                  })
                ) : (
                  <Typography
                    sx={{
                      px: 0.6,
                      py: 0.2,
                      fontSize: 11,
                      color: "rgba(234,231,226,0.38)",
                    }}
                  >
                    No archived projects
                  </Typography>
                )}
              </Box>
            )}
            <Divider
              sx={{
                mt: 1.5,
                mb: 0.25,
                borderColor: "rgba(255,255,255,0.15)",
              }}
            />
            </>
            )}
            {isProjectsCompact && (
              <Divider
                sx={{
                  mt: 0.65,
                  mb: 0.15,
                  borderColor: "rgba(255,255,255,0.12)",
                }}
              />
            )}
          </>
        )}
      </Box>

      <Menu
        anchorEl={projectMenuAnchor}
        open={Boolean(projectMenuAnchor)}
        onClose={closeProjectMenu}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{
          sx: {
            ml: 0.5,
            minWidth: 148,
            borderRadius: "10px",
            background: homeGlassHero.rest,
            backdropFilter: HOME_GLASS_BACKDROP,
            WebkitBackdropFilter: HOME_GLASS_BACKDROP,
            border: HOME_GLASS_BORDER,
            boxShadow: "0 12px 28px rgba(0,0,0,0.28)",
            "& .MuiMenuItem-root": {
              fontSize: 12,
              py: 0.85,
              color: "rgba(234,231,226,0.92)",
            },
          },
        }}
      >
        <MenuItem onClick={handleProjectMenuEdit} dense>
          <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
            <DriveFileRenameOutlineIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem
          onClick={handleProjectMenuDelete}
          dense
          sx={{ color: "rgba(255,200,200,0.95)" }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <Popover
        open={Boolean(deleteAnchorEl && pendingDeleteProject)}
        anchorEl={deleteAnchorEl}
        onClose={closeDeletePopover}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{
          sx: {
            ml: 0.8,
            px: 1.2,
            py: 1.2,
            borderRadius: "10px",
            background: "transparent",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.12)",
            minWidth: 220,
          },
        }}
      >
        <Typography sx={{ fontSize: 11, color: "rgba(234,231,226,0.75)", mb: 0.8 }}>
          Delete this project?
        </Typography>
        <Typography
          sx={{
            fontSize: 11.5,
            color: "#fff",
            mb: 1,
            maxWidth: 210,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={pendingDeleteProject?.title || ""}
        >
          {pendingDeleteProject?.title || "Project"}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.8 }}>
          <Button
            onClick={closeDeletePopover}
            size="small"
            sx={{
              minWidth: 52,
              height: 26,
              px: 1.2,
              color: "rgba(234,231,226,0.8)",
              fontSize: 10.5,
              borderRadius: "7px",
              textTransform: "none",
              border: "1px solid rgba(255,255,255,0.16)",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteProject}
            size="small"
            sx={{
              minWidth: 52,
              height: 26,
              px: 1.2,
              fontSize: 10.5,
              borderRadius: "7px",
              textTransform: "none",
              color: "#fff",
              background: "linear-gradient(135deg, #a855f7, #d946ef)",
              "&:hover": {
                background: "linear-gradient(135deg, #9333ea, #c026d3)",
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Popover>
    </>
  );
}
